import { Button, DatePicker, Form, Input, Select, Space } from "antd"
import { forwardRef, useImperativeHandle, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs, { Dayjs } from "dayjs"
import { SyncOutlined } from "@ant-design/icons"

const { RangePicker } = DatePicker
const { Option } = Select

export interface GiftCardFilterRef {
	filter?: GiftCardFilter
}

export interface GiftCardFilter {
	createdAt?: [Dayjs, Dayjs]
	movementType?: string
	search?: string
}

interface GiftCardDataFilterProps {
	onSubmit?: (values: GiftCardFilter) => void
	onReset?: () => void
}

const GiftCardDataFilter = forwardRef<GiftCardFilterRef, GiftCardDataFilterProps>(({ onSubmit, onReset }, ref) => {
	const [form] = Form.useForm()
	const { t } = useTranslation("transactions", { keyPrefix: "giftCardFilter" })
	const [filter, setFilter] = useState<GiftCardFilter>()
	const [submitting, setSubmitting] = useState(false)
	const [reseting, setReseting] = useState(false)

	useImperativeHandle(ref, () => ({
		filter
	}))

	const handleSubmit = async () => {
		setSubmitting(true)
		try {
			const values = form.getFieldsValue()
			const filterData: GiftCardFilter = {
				createdAt: values.createdAt,
				movementType: values.movementType,
				search: values.search
			}
			setFilter(filterData)
			onSubmit && onSubmit(filterData)
		} catch (error) {
			console.log("@@gift card filter error@@", error)
		} finally {
			setSubmitting(false)
		}
	}

	const handleReset = async () => {
		if (reseting) return
		setReseting(true)
		try {
			form.resetFields()
			setFilter(undefined)
			onReset && onReset()
		} catch (error) {
			console.log("@@gift card filter error@@", error)
		} finally {
			setReseting(false)
		}
	}

	return (
		<Form layout="vertical" form={form} onFinish={handleSubmit} onReset={handleReset}>
			<Space direction="horizontal" align="end" size={"large"} className="flex gap-8">
				<Form.Item name="createdAt" label={t("dateRange")}>
					<RangePicker
						allowClear
						style={{ width: "100%" }}
						format="YYYY-MM-DD"
						onChange={dates => {
							if (dates == null) {
								setFilter({ ...filter, createdAt: undefined })
								return
							}
							const dateFrom = dayjs(dates?.[0]?.toDate())
							const dateTo = dayjs(dates?.[1]?.toDate())
							setFilter({
								...filter,
								createdAt: [dateFrom, dateTo]
							})
						}}
					/>
				</Form.Item>
				<Form.Item name="movementType" label={t("movementType")}>
					<Select
						placeholder={t("selectMovementType")}
						allowClear
						style={{ minWidth: 180 }}
						onChange={movementType => setFilter({ ...filter, movementType })}
					>
						<Option value="creation">{t("movementTypes.creation")}</Option>
						<Option value="usage">{t("movementTypes.usage")}</Option>
						<Option value="refund">{t("movementTypes.refund")}</Option>
						<Option value="adjustment">{t("movementTypes.adjustment")}</Option>
					</Select>
				</Form.Item>
				<Form.Item name="search" label={t("search")}>
					<Input
						placeholder={t("searchPlaceholder")}
						allowClear
						style={{ minWidth: 200 }}
						onChange={e => setFilter({ ...filter, search: e.target.value })}
					/>
				</Form.Item>
				<Form.Item>
					<div className="flex flex-row">
						<Button className="mr-[0.15rem]" loading={submitting} htmlType="submit" type="primary">
							{t("filter")}
						</Button>
						<Button
							className={reseting ? "opacity-70" : ""}
							htmlType="reset"
							disabled={!filter?.movementType && !filter?.createdAt && !filter?.search}
							icon={<SyncOutlined className={reseting ? "animate-spin" : ""} />}
							type="primary"
						/>
					</div>
				</Form.Item>
			</Space>
		</Form>
	)
})

export default GiftCardDataFilter
